import React, { useContext, useState } from 'react';
import { CartContext } from '../../../context/CartContext';
import { ChevronDown, ChevronRight, Clock, UtensilsCrossed, Package, CheckCircle2, Hash, IndianRupee, XCircle, AlertTriangle, RefreshCw, Flame, Send, Printer } from 'lucide-react';
import CancelKOTReceiptDisplay from './CancelKOTReceiptDisplay';
import KOTReceiptDisplay from './KOTReceiptDisplay';
import { useKOT } from '../../../hooks/useKOT';
import { useAuth } from '../../../context/AuthContext';
import { P } from '../../../config/permissionCodes';
import { toast } from 'react-hot-toast';

export default function KOTOrders() {
  const { currentTableId, currenttableSplit, isBillPrinted } = useContext(CartContext);
  const { hasPermission } = useAuth();
  const canCancel = hasPermission(P.POS.DINE_IN.CANCEL);

  // ✅ Default to "A" if no split is specified
  const tableSplit = currenttableSplit || 'A';

  // ✅ Fetch active orders from API instead of localStorage
  const { activeOrders, loading, error, cancelKOT: cancelKOTAPI, refreshActiveOrders } = useKOT(
    currentTableId,
    tableSplit,
    { skipIfNew: true } // ✅ Skip API call for brand new orders
  );

  // State for refresh loading indicator
  const [refreshing, setRefreshing] = useState(false);

  const [expandedKOTs, setExpandedKOTs] = useState(new Set());

  // ⭐ NEW: Cancel KOT state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [kotToCancel, setKotToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // ⭐ NEW: Cancel receipt display state
  const [showCancelReceipt, setShowCancelReceipt] = useState(false);
  const [cancelReceiptData, setCancelReceiptData] = useState(null);

  const [showReprintReceipt, setShowReprintReceipt] = useState(false);
  const [reprintData, setReprintData] = useState(null);

  // ✅ Use API orders instead of localStorage
  // API now returns orders sorted by createdDateTime DESC (latest first)
  const kots = activeOrders || [];

  const toggleKOT = (kotId) => {
    setExpandedKOTs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(kotId)) {
        newSet.delete(kotId);
      } else {
        newSet.add(kotId);
      }
      return newSet;
    });
  };

  const getStationBadgeColor = (station) => {
    // Normalize station name for color matching
    const normalizedStation = station?.toLowerCase()?.trim() || '';
    const colors = {
      'main kitchen': 'bg-blue-50 text-blue-600 border-blue-200',
      'kitchen': 'bg-blue-50 text-blue-600 border-blue-200',
      'tandoor': 'bg-orange-50 text-orange-600 border-orange-200',
      'grill': 'bg-red-50 text-red-600 border-red-200',
      'bar': 'bg-purple-50 text-purple-600 border-purple-200',
      'bakery': 'bg-pink-50 text-pink-600 border-pink-200',
      'cold kitchen': 'bg-cyan-50 text-cyan-600 border-cyan-200',
      'hot kitchen': 'bg-red-50 text-red-600 border-red-200',
    };
    return colors[normalizedStation] || 'bg-slate-50 text-slate-600 border-slate-200';
  };

  const getStatusBadge = (status) => {
    const badges = {
      'sent': { label: 'Sent', color: 'bg-blue-500 text-white', icon: Package },
      'preparing': { label: 'Preparing', color: 'bg-amber-500 text-white', icon: UtensilsCrossed },
      'ready': { label: 'Ready', color: 'bg-green-500 text-white', icon: CheckCircle2 },
      'served': { label: 'Served', color: 'bg-slate-400 text-white', icon: CheckCircle2 },
      'cancelled': { label: 'Cancelled', color: 'bg-red-600 text-white', icon: XCircle }, // ⭐ NEW
    };
    return badges[status] || badges['sent'];
  };

  // Get item status icon config for individual items
  const getItemStatusIcon = (itemStatus) => {
    const statusIcons = {
      'sent': {
        icon: Send,
        color: 'text-blue-500',
        tooltip: 'Sent to kitchen',
        animated: false
      },
      'preparing': {
        icon: Flame,
        color: 'text-amber-500',
        tooltip: 'Being prepared',
        animated: true
      },
      'ready': {
        icon: CheckCircle2,
        color: 'text-green-500',
        tooltip: 'Ready to serve',
        animated: false
      },
      'served': {
        icon: CheckCircle2,
        color: 'text-slate-400',
        tooltip: 'Served',
        animated: false
      },
      'cancelled': {
        icon: XCircle,
        color: 'text-red-500',
        tooltip: 'Cancelled',
        animated: false
      },
    };
    return statusIcons[itemStatus] || statusIcons['sent'];
  };

  const formatKOTTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeSinceKOT = (timestamp) => {
    const now = new Date();
    const kotTime = new Date(timestamp);
    const diffMs = now - kotTime;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;

    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    return `${diffHours}h ${remainingMins}m ago`;
  };

  const calculateKOTTotal = (items) => {
    return items.reduce((total, item) => {
      const price = item.finalPrice || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  // ⭐ NEW: Handle cancel button click
  const handleCancelClick = (kot) => {
    setKotToCancel(kot);
    setCancelReason('');
    setShowCancelConfirm(true);
  };

  // - Active KOTs reopen the normal KOT receipt in print-only mode (no re-send to kitchen)
  const handleReprintClick = (kot) => {
    const tableName = kot.tableNumber || `Table ${currentTableId}${currenttableSplit ? `-${currenttableSplit}` : ''}`;

    if (kot.status === 'cancelled') {
      setCancelReceiptData({
        kotNumber: kot.orderNumber || kot.kotNumber || 'N/A',
        table: { name: tableName },
        items: kot.items || [],
        reason: kot.cancellationReason || ''
      });
      setCancelReason(kot.cancellationReason || '');
      setShowCancelReceipt(true);
      return;
    }

    setReprintData({
      kotNumber: kot.kotNumber,
      table: { name: tableName },
      items: kot.items || [],
      notes: kot.notes,
    });
    setShowReprintReceipt(true);
  };

  // ✅ Handle refresh orders
  const handleRefreshOrders = async () => {
    setRefreshing(true);
    try {
      await refreshActiveOrders();
    } finally {
      setRefreshing(false);
    }
  };

  // ⭐ NEW: Confirm cancellation
  const confirmCancelKOT = async () => {
    if (!cancelReason.trim()) {
      return; // Reason is required
    }

    if (isBillPrinted) {
      toast.error('Bill already printed — this KOT can no longer be cancelled');
      setShowCancelConfirm(false);
      setKotToCancel(null);
      return;
    }

    try {
      // ✅ Use API to cancel KOT
      await cancelKOTAPI(kotToCancel.id, cancelReason);

      // Prepare data for cancel receipt - properly structure it for the receipt component
      setCancelReceiptData({
        kotNumber: kotToCancel.orderNumber || 'N/A',
        table: {
          name: kotToCancel.tableNumber || `Table ${currentTableId}${currenttableSplit ? `-${currenttableSplit}` : ''}`
        },
        items: kotToCancel.items || [], // Items from API with kitchenStationName
        reason: cancelReason
      });

      setShowCancelConfirm(false);
      setShowCancelReceipt(true);
    } catch (error) {
      console.error('Failed to cancel KOT:', error);
      // Error toast is already shown by useKOT hook
    }
  };

  // ✅ Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 bg-white rounded-lg border-2 border-dashed border-slate-200">
        <div className="bg-blue-100 rounded-full p-6 mb-4 animate-pulse">
          <UtensilsCrossed size={40} className="text-blue-500" />
        </div>
        <p className="text-slate-700 font-semibold mb-1">Loading orders...</p>
        <p className="text-slate-500 text-sm">Fetching active orders from kitchen</p>
      </div>
    );
  }

  // ✅ Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 bg-white rounded-lg border-2 border-dashed border-red-200">
        <div className="bg-red-100 rounded-full p-6 mb-4">
          <AlertTriangle size={40} className="text-red-500" />
        </div>
        <p className="text-red-700 font-semibold mb-1">Failed to load orders</p>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <>
      {/* ✅ Empty state */}
      {kots.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center p-8 bg-white rounded-lg border-2 border-dashed border-slate-200">
          <div className="bg-slate-100 rounded-full p-6 mb-4">
            <UtensilsCrossed size={40} className="text-slate-400" />
          </div>
          <p className="text-slate-700 font-semibold mb-1">No orders sent to kitchen yet</p>
          <p className="text-slate-500 text-sm">
            Items from the draft will appear here after clicking "Send to Kitchen"
          </p>
        </div>
      ) : (
        <div className="space-y-4">
        {/* Section Header */}
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 uppercase tracking-wide">
          <CheckCircle2 size={16} className="text-teal-600" />
          <span>Sent to Kitchen</span>
          <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-bold">
            {kots.length}
          </span>
          {/* Refresh Button */}
          <button
            onClick={handleRefreshOrders}
            disabled={refreshing}
            className="ml-auto p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh orders"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* KOT Cards */}
        {kots.map((kot) => {
          const isExpanded = expandedKOTs.has(kot.id);
          const statusBadge = getStatusBadge(kot.status);
          const StatusIcon = statusBadge.icon;
          const totalItems = kot.items.reduce((sum, item) => sum + item.quantity, 0);
          const kotTotal = calculateKOTTotal(kot.items);

          // ⭐ NEW: Check if cancelled
          const isCancelled = kot.status === 'cancelled';

          return (
            <div
              key={kot.id}
              onClick={() => toggleKOT(kot.id)}
              className={`bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transition-all ${
                isCancelled
                  ? 'border-2 border-red-300 bg-red-50/50 hover:bg-red-100' // ⭐ Softer RED styling for cancelled
                  : 'border-2 border-slate-200 hover:bg-slate-50 hover:shadow-lg'
              }`}
            >
              {/* KOT Header */}
              <div className="w-full px-4 py-3">
                {/* Line 1: KOT #, Time, Elapsed, Amount */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-sm font-medium ${isCancelled ? 'text-red-700 line-through' : 'text-slate-500'}`}>
                    #{kot.kotNumber}
                  </span>
                  <span className={isCancelled ? 'text-red-300' : 'text-slate-300'}>•</span>
                  <span className={`font-bold text-sm ${isCancelled ? 'text-red-900 line-through' : 'text-slate-900'}`}>
                    {formatKOTTime(kot.timestamp)}
                  </span>
                  <span className={`text-xs ${isCancelled ? 'text-red-600' : 'text-slate-500'}`}>
                    ({getTimeSinceKOT(kot.timestamp)})
                  </span>

                  {/* ⭐ KOT Total Amount (just amount, no cancel button) */}
                  <div className={`ml-auto flex items-center gap-1 rounded-lg px-2 py-1 border ${
                    isCancelled
                      ? 'bg-red-100 border-red-300 line-through'
                      : 'bg-teal-50 border-teal-200'
                  }`}>
                    <IndianRupee size={14} className={isCancelled ? 'text-red-700' : 'text-teal-700'} />
                    <span className={`font-bold text-sm ${isCancelled ? 'text-red-700' : 'text-teal-700'}`}>
                      {kotTotal.toFixed(0)}
                    </span>
                  </div>
                </div>

                {/* Line 2: Expand Arrow, Items, Status, Reprint/Cancel Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Small Expand Icon */}
                  <div className={isCancelled ? 'text-red-400' : 'text-slate-400'}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </div>

                  {/* Status Dot */}
                  <div className={`w-2 h-2 rounded-full ${statusBadge.color} flex-shrink-0`}></div>

                  {/* Items Count */}
                  <span className={`text-xs font-medium ${isCancelled ? 'text-red-700' : 'text-slate-600'}`}>
                    {totalItems} {totalItems === 1 ? 'item' : 'items'}
                  </span>

                  {/* Status Badge */}
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                    <StatusIcon size={10} />
                    <span>{statusBadge.label}</span>
                  </div>

                  {/* Reprint + Cancel action buttons */}
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); 
                        handleReprintClick(kot);
                      }}
                      className="p-1.5 bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-md transition-colors border border-teal-300 hover:border-teal-400 flex items-center gap-1"
                      title="Reprint KOT receipt"
                    >
                      <Printer size={14} />
                      <span className="text-xs font-medium">Reprint</span>
                    </button>

                    {/* Cancel Button — disabled once the bill has been printed */}
                    {!isCancelled && canCancel && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent toggle when clicking cancel
                          if (isBillPrinted) return;
                          handleCancelClick(kot);
                        }}
                        disabled={isBillPrinted}
                        className={`p-1.5 rounded-md transition-colors border flex items-center gap-1 ${
                          isBillPrinted
                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : 'bg-red-100 hover:bg-red-200 text-red-600 border-red-300 hover:border-red-400'
                        }`}
                        title={isBillPrinted ? 'Bill already printed — KOT can no longer be cancelled' : 'Cancel this KOT'}
                      >
                        <XCircle size={14} />
                        <span className="text-xs font-medium">Cancel</span>
                      </button>
                    )}
                  </div>
                </div>
              </div> 

              {/*  Cancellation Info Banner (if cancelled) */}
              {isCancelled && (
                <div className="bg-red-100 border-t-2 border-red-400 px-4 py-2 flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 flex-wrap text-xs">
                    <span className="text-red-800">
                      <span className="font-semibold">Reason:</span> {kot.cancellationReason}
                    </span>
                    <span className="text-red-800">•</span>
                    <span className="text-red-700">
                      Cancelled at: {formatKOTTime(kot.cancelledAt)}
                    </span>
                  </div>
                </div>
              )}

              {/* Expanded KOT Details */}
              {isExpanded && (
                <div className="border-t border-slate-100">
                  {/* Order Items Header */}
                  <div className={`flex items-center justify-between px-4 py-2.5 text-white ${
                    isCancelled ? 'bg-red-600' : 'bg-teal-600'
                  }`}>
                    <div className="flex items-center gap-2">
                      <UtensilsCrossed size={14} />
                      <span className="font-semibold text-sm">Order Items</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs bg-white bg-opacity-20 px-2 py-0.5 rounded font-medium">
                        {kot.items.length} {kot.items.length === 1 ? 'item' : 'items'}
                      </span>
                      <div className="flex items-center gap-1 bg-white bg-opacity-20 px-2 py-0.5 rounded font-medium text-xs">
                        <IndianRupee size={12} />
                        <span>{kotTotal.toFixed(0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Items List - Horizontal Layout */}
                  <div className="bg-white border border-slate-200 rounded-b-lg">
                    {kot.items.map((item, idx) => {
                      const itemTotal = (item.finalPrice || item.price) * item.quantity;

                      return (
                        <div
                          key={idx}
                          className={`px-4 py-3 border-b border-slate-100 last:border-b-0 transition-colors ${
                            isCancelled
                              ? 'hover:bg-red-50 opacity-75'
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          {/* Top Row: Item Name | Quantity | Price */}
                          <div className="flex items-center justify-between gap-3 mb-1">
                            {/* LEFT: Status Icon + Item Name with Station */}
                            <div className="flex items-center gap-2 flex-1">
                              {/* Item Status Icon */}
                              {(() => {
                                const statusIcon = getItemStatusIcon(isCancelled ? 'cancelled' : item.itemStatus);
                                const StatusIconComponent = statusIcon.icon;
                                return (
                                  <span title={statusIcon.tooltip} className="flex-shrink-0">
                                    <StatusIconComponent
                                      size={16}
                                      className={`${statusIcon.color} ${statusIcon.animated && !isCancelled ? 'animate-flame' : ''}`}
                                    />
                                  </span>
                                );
                              })()}
                              <h4 className={`font-semibold text-sm ${isCancelled ? 'text-red-900' : 'text-slate-900'}`}>
                                {item.name}
                              </h4>
                              {/* Station Badge (compact) */}
                              {item.kitchenStationName && (
                                <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                                  isCancelled
                                    ? 'bg-red-100 text-red-600 border border-red-200'
                                    : getStationBadgeColor(item.kitchenStationName)
                                }`}>
                                  {item.kitchenStationName}
                                </span>
                              )}
                            </div>

                            {/* CENTER: Quantity Badge */}
                            <div className={`flex items-center gap-1 px-2 py-0.5 rounded font-semibold text-xs ${
                              isCancelled ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'
                            }`}>
                              <span>×</span>
                              <span>{item.quantity}</span>
                            </div>

                            {/* RIGHT: Pricing */}
                            <div className={`flex flex-col items-end min-w-[80px] ${isCancelled ? 'text-red-700' : 'text-slate-700'}`}>
                              {/* Unit Price (Top) */}
                              <div className="text-[10px] font-medium opacity-60">
                                ₹{(item.finalPrice || item.price).toFixed(0)} each
                              </div>
                              {/* Total Price (Bottom) */}
                              <div className="flex items-center gap-0.5">
                                <IndianRupee size={12} className="font-bold" />
                                <span className="text-base font-black">
                                  {itemTotal.toFixed(0)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Bottom Row: Customizations (inline with pipe separator) */}
                          {item.customization?.customInstructions && (
                            <div className={`text-[11px] font-medium ${isCancelled ? 'text-red-700' : 'text-slate-600'}`}>
                              {item.customization.customInstructions}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Chef Notes */}
                  {kot.notes && (
                    <div className={`mx-4 mb-4 mt-3 p-3 rounded-lg border ${
                      isCancelled
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        <span className="text-lg">👨‍🍳</span>
                        <div className="flex-1">
                          <p className={`text-xs font-semibold uppercase mb-0.5 ${
                            isCancelled ? 'text-red-900' : 'text-amber-900'
                          }`}>
                            Chef Notes
                          </p>
                          <p className={`text-sm ${isCancelled ? 'text-red-900' : 'text-amber-900'}`}>
                            {kot.notes}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}

      {/*  Cancel Confirmation Modal */}
      {showCancelConfirm && kotToCancel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Cancel KOT #{kotToCancel.kotNumber}?</h3>
                <p className="text-sm text-slate-600">
                  This will remove all items from the bill and notify the kitchen to stop preparation.
                </p>
              </div>
            </div>

            {/* Reason Input */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Cancellation Reason <span className="text-red-600">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="E.g., Customer changed order, Wrong items sent, Kitchen mistake..."
                className="w-full px-3 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows={3}
                autoFocus
              />
            </div>

            {/* Items Preview */}
            <div className="bg-slate-50 rounded-lg p-3 max-h-40 overflow-y-auto">
              <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Items to Cancel:</p>
              {kotToCancel.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-xs text-slate-700 py-1">
                  <span>{item.name}</span>
                  <span className="font-semibold">× {item.quantity}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  setKotToCancel(null);
                  setCancelReason('');
                }}
                className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition"
              >
                Keep KOT
              </button>
              <button
                onClick={confirmCancelKOT}
                disabled={!cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Yes, Cancel KOT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Receipt Display (also used for reprinting void slips) */}
      {showCancelReceipt && cancelReceiptData && (
        <CancelKOTReceiptDisplay
          onClose={() => setShowCancelReceipt(false)}
          kotData={cancelReceiptData}
          cancelReason={cancelReason}
        />
      )}

      {/* Reprint Receipt Display (for active/non-cancelled KOTs) */}
      {showReprintReceipt && reprintData && (
        <KOTReceiptDisplay
          onClose={() => {
            setShowReprintReceipt(false);
            setReprintData(null);
          }}
          kotData={reprintData}
          isReprint={true}
        />
      )}
    </>
  );
}